import {
    afterRenderEffect,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    model,
    signal,
    viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';

export interface SegmentedOption<T> {
    readonly label: string;
    readonly value: T;
    /** Shown after the label. Leave it out where the number gives the user nothing to act on. */
    readonly count?: number;
}

interface RenderedOption<T> extends SegmentedOption<T> {
    /** SelectButton names each button after its label, which would hide the count from a screen reader. */
    readonly accessibleLabel: string;
}

/**
 * One choice out of a few, side by side: the tabs above a list. PrimeNG's SelectButton does the
 * work (a group of toggle buttons, Enter and Space to pick); this adds the count after the label.
 * The look is global, in styles.css, because the buttons are PrimeNG's own markup.
 *
 * Wider than a phone, the group scrolls sideways. A tab cut at the edge gave no sign that more
 * followed it, so each edge fades while tabs are hidden past it.
 */
@Component({
    selector: 'app-segmented-filter',
    imports: [FormsModule, SelectButtonModule],
    host: {
        '[class.segmented--hidden-before]': 'hiddenBefore()',
        '[class.segmented--hidden-after]': 'hiddenAfter()'
    },
    template: `
        <p-selectbutton
            #group
            class="app-segmented"
            styleClass="app-segmented__option"
            [attr.aria-label]="ariaLabel()"
            [options]="renderedOptions()"
            optionLabel="accessibleLabel"
            optionValue="value"
            [allowEmpty]="false"
            [ngModel]="value()"
            (ngModelChange)="value.set($event)"
            (scroll)="measureOverflow()">
            <ng-template #item let-option>
                <span>{{ option.label }}</span>
                @if (option.count !== undefined) {
                    <span class="app-segmented__count">{{ option.count }}</span>
                }
            </ng-template>
        </p-selectbutton>
    `,
    styles: `
        :host {
            position: relative;
            isolation: isolate;
            display: block;
            max-width: 100%;
        }

        /*
         * Inside the group's 1px border, so the border itself stays whole, and above the tabs, which
         * PrimeNG positions. 2rem is the group's scroll-padding in styles.css.
         */
        :host::before,
        :host::after {
            content: '';
            position: absolute;
            z-index: 1;
            inset-block: 1px;
            width: 2rem;
            opacity: 0;
            pointer-events: none;
            transition: opacity 150ms ease;
        }

        :host::before {
            inset-inline-start: 1px;
            border-start-start-radius: 0.5625rem;
            border-end-start-radius: 0.5625rem;
            background: linear-gradient(to right, var(--p-content-background) 30%, transparent);
        }

        :host::after {
            inset-inline-end: 1px;
            border-start-end-radius: 0.5625rem;
            border-end-end-radius: 0.5625rem;
            background: linear-gradient(to left, var(--p-content-background) 30%, transparent);
        }

        :host(.segmented--hidden-before)::before,
        :host(.segmented--hidden-after)::after {
            opacity: 1;
        }

        @media (prefers-reduced-motion: reduce) {
            :host::before,
            :host::after {
                transition: none;
            }
        }
    `
})
export class SegmentedFilterComponent<T> {
    readonly options = input.required<readonly SegmentedOption<T>[]>();
    readonly value = model.required<T>();
    /** What the group chooses, e.g. "Situação do pedido". */
    readonly ariaLabel = input.required<string>();

    protected readonly renderedOptions = computed<RenderedOption<T>[]>(() =>
        this.options().map(option => ({
            ...option,
            accessibleLabel: option.count === undefined ? option.label : `${option.label} (${option.count})`
        }))
    );

    /** Whether tabs are scrolled out of view before the first visible one, and after the last. */
    protected readonly hiddenBefore = signal(false);
    protected readonly hiddenAfter = signal(false);

    private readonly group = viewChild.required<string, ElementRef<HTMLElement>>('group', { read: ElementRef });

    constructor() {
        // The group keeps its width while a count arrives or the web font loads and widens the tabs inside it,
        // so the tabs are watched as well as the group.
        const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.measureOverflow());
        inject(DestroyRef).onDestroy(() => resize?.disconnect());

        afterRenderEffect(() => {
            this.renderedOptions();
            const group = this.group().nativeElement;

            resize?.disconnect();
            resize?.observe(group);
            Array.from(group.children).forEach(tab => resize?.observe(tab));
            this.measureOverflow();
        });

        // A tab picked while half under the fade, or chosen before the list opened, is brought fully into view.
        afterRenderEffect(() => {
            const index = this.renderedOptions().findIndex(option => option.value === this.value());
            const tab = this.group().nativeElement.children.item(index);

            tab?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        });
    }

    protected measureOverflow(): void {
        const group = this.group().nativeElement;
        const hiddenWidth = group.scrollWidth - group.clientWidth;

        // A fractional width leaves a pixel of overflow that hides nothing.
        this.hiddenBefore.set(group.scrollLeft > 1);
        this.hiddenAfter.set(hiddenWidth - group.scrollLeft > 1);
    }
}
